/**
 * iter167 — Native-shell abstraction for GPS tracking.
 *
 * Detects whether the app is running inside the Capacitor wrapper
 * (iOS/Android native build) and routes GPS reads through the right
 * APIs:
 *
 *   • Web build → `navigator.geolocation.getCurrentPosition` (foreground
 *     only — iOS Safari suspends timers when backgrounded).
 *   • Capacitor build → `@capacitor-community/background-geolocation`
 *     watchPosition, which keeps a foreground service alive on Android
 *     and uses `CLLocationManager` with `requestAlwaysAuthorization`
 *     on iOS so the OS keeps delivering updates even when the screen
 *     is off / app is backgrounded.
 *
 * The MyWorkOrders page calls `nativeShell.startTracking` / `stopTracking`
 * without caring which path it took.  Callbacks emit the same
 * `{lat, lng, accuracy_m, captured_at}` shape on both platforms so
 * the upstream payload to /api/dispatch/tech-location stays identical.
 *
 * Reference: https://github.com/capacitor-community/background-geolocation
 */

let _capacitorMod = null;        // lazy-imported @capacitor/core
let _bgGeoMod = null;             // lazy-imported background-geolocation
let _activeWatcherId = null;      // ID returned by addWatcher / setInterval
let _activeBackend = null;        // "native" | "web"

/**
 * Are we running inside the Capacitor native shell?
 *
 * Caches the answer because the check involves a dynamic import and we
 * don't want to re-await it on every tick.
 */
export async function isNativeShell() {
  if (typeof window === "undefined") return false;
  if (window.Capacitor && typeof window.Capacitor.isNativePlatform === "function") {
    return Boolean(window.Capacitor.isNativePlatform());
  }
  // Capacitor injects `window.Capacitor` only after the bridge boots —
  // on the very first SPA load before bridge-ready it can briefly be
  // undefined.  Try the lazy import as a fallback.
  try {
    if (!_capacitorMod) _capacitorMod = await import("@capacitor/core");
    return Boolean(_capacitorMod.Capacitor?.isNativePlatform?.());
  } catch {
    return false;
  }
}


/**
 * Start emitting positions to `onUpdate({lat, lng, accuracy_m, captured_at})`.
 *
 * Returns a cancel function the caller stores and invokes on unmount.
 * Same contract as the web fallback so consumers don't need to branch.
 *
 * @param {{
 *   onUpdate: (coords: {lat:number,lng:number,accuracy_m:number,captured_at:string}) => void,
 *   onError?: (err: Error|GeolocationPositionError) => void,
 *   minDistanceM?: number,      // skip pings within this distance of last
 *   foregroundIntervalMs?: number, // how often the WEB fallback polls
 * }} opts
 */
export async function startTracking({
  onUpdate,
  onError,
  minDistanceM = 25,
  foregroundIntervalMs = 60_000,
}) {
  stopTracking(); // idempotent — bail any prior watcher first.

  const useNative = await isNativeShell();

  if (useNative) {
    // ── Native: addWatcher keeps GPS alive in the background. ──
    try {
      if (!_bgGeoMod) {
        _bgGeoMod = await import("@capacitor-community/background-geolocation");
      }
      const { BackgroundGeolocation } = _bgGeoMod;
      _activeWatcherId = await BackgroundGeolocation.addWatcher(
        {
          backgroundMessage:
            "ServiceOps is keeping your location current for dispatch.",
          backgroundTitle: "ServiceOps · Tracking",
          requestPermissions: true,
          stale: false,
          distanceFilter: minDistanceM,
        },
        (location, error) => {
          if (error) {
            if (onError) onError(error);
            return;
          }
          if (!location) return;
          onUpdate({
            lat: location.latitude,
            lng: location.longitude,
            accuracy_m: location.accuracy,
            captured_at: new Date(location.time || Date.now()).toISOString(),
            source: "native",
          });
        },
      );
      _activeBackend = "native";
      return stopTracking;
    } catch (e) {
      // If the native plugin failed to attach (e.g. permission denied,
      // device GPS off), fall back to the web path so the dispatcher at
      // least gets foreground pings.
      if (onError) onError(e);
      _activeBackend = null;
    }
  }

  // ── Web: navigator.geolocation, foreground-only. ──
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    if (onError) onError(new Error("Geolocation not available"));
    return stopTracking;
  }
  const send = () =>
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onUpdate({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          captured_at: new Date(pos.timestamp || Date.now()).toISOString(),
          source: "web",
        });
      },
      (err) => {
        if (onError) onError(err);
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 5_000 },
    );
  // Fire immediately so dispatchers see the tech in seconds, not minutes.
  send();
  _activeWatcherId = setInterval(send, foregroundIntervalMs);
  _activeBackend = "web";
  return stopTracking;
}


/**
 * Cancel any active watcher.  Safe to call multiple times.
 */
export function stopTracking() {
  if (_activeWatcherId == null) return;
  if (_activeBackend === "native" && _bgGeoMod?.BackgroundGeolocation) {
    try {
      _bgGeoMod.BackgroundGeolocation.removeWatcher({ id: _activeWatcherId });
    } catch {
      /* best-effort */
    }
  } else if (_activeBackend === "web") {
    try { clearInterval(_activeWatcherId); } catch { /* ignore */ }
  }
  _activeWatcherId = null;
  _activeBackend = null;
}


/**
 * Update the foreground polling cadence on the WEB backend.  Native
 * doesn't need this — the platform decides when to fire based on
 * `distanceFilter` and motion.
 */
export function setForegroundInterval(ms) {
  if (_activeBackend !== "web") return;
  if (_activeWatcherId == null) return;
  clearInterval(_activeWatcherId);
  _activeWatcherId = setInterval(() => {
    // Use a one-shot getCurrentPosition because watch already returned;
    // callers set up their own emitter.
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(() => {}, () => {}, {
      enableHighAccuracy: true, timeout: 15_000, maximumAge: 5_000,
    });
  }, ms);
}


/**
 * Read-only: which backend is currently active?  UI uses this to show
 * "Background tracking active" vs "Foreground-only" copy.
 */
export function activeBackend() {
  return _activeBackend;
}
